import Typography from '@mui/material/Typography'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CategorizedTreatmentsWithPartialTreatments } from '@customTypes/CategorizedTreatment'
import { HandleSelectTreatment } from '@customTypes/HandleSelectTreatment'
import { TreatmentCard } from './TreatmentCard'
import useStyles from './styles'
import { TEST_IDS } from 'src/constants/testIds'
import { ReactComponent as ToggleCategoryIcon } from 'src/images/toggleCategoryIcon.svg'
import { isEmpty } from 'lodash'
import clsx from 'clsx'
import { useIsSuggestedTreatment } from '@hooks/useIsSuggestedTreatment'

interface Props {
  categorizedTreatments: CategorizedTreatmentsWithPartialTreatments[]
  handleSelectTreatment: HandleSelectTreatment
  selectedTreatmentId: string | undefined
}

export const Body = ({ categorizedTreatments, selectedTreatmentId, handleSelectTreatment }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [categoriesToggle, setCategoriesToggle] = useState<Record<string, boolean>>({})
  const isSuggestedTreatmentTest = useIsSuggestedTreatment()

  useEffect(() => {
    if (isEmpty(categoriesToggle) && !isEmpty(categorizedTreatments)) {
      const slectedTreatmentCategoryIndex = categorizedTreatments.findIndex((c) =>
        c.treatmentBundles.some((t) => t.referenceId === selectedTreatmentId)
      )

      const toggleState = categorizedTreatments.reduce((prev, curr, index) => {
        const shouldCategoryBeOpen = (() => {
          // If experiment is not running - open all
          if (!isSuggestedTreatmentTest) return true
          // If treatment is selected only first category with treatment should be open
          if (slectedTreatmentCategoryIndex > -1) return index === slectedTreatmentCategoryIndex
          // By default only first category is open
          return index === 0
        })()

        return { ...prev, [curr.titleKey]: shouldCategoryBeOpen }
      }, {})

      setCategoriesToggle(toggleState)
    }
  }, [categorizedTreatments, categoriesToggle, isSuggestedTreatmentTest, selectedTreatmentId])

  const toggleCategory = (categoryTitle: string) =>
    setCategoriesToggle({ ...categoriesToggle, [categoryTitle]: !categoriesToggle[categoryTitle] })

  return (
    <div className={classes.pageContainer}>
      <div data-test={TEST_IDS.treatmentsContainer} className={classes.treatmentsContainer}>
        {categorizedTreatments.map((treatmentCategory) => (
          <React.Fragment key={treatmentCategory.titleKey}>
            <div
              data-test={TEST_IDS.treatmentCategory}
              className={clsx(classes.categoryContainer, {
                [classes.categoryContainerCollapsed]: !categoriesToggle[treatmentCategory.titleKey],
                [classes.clickableCategoryContainer]: isSuggestedTreatmentTest,
              })}
              onClick={() => isSuggestedTreatmentTest && toggleCategory(treatmentCategory.titleKey)}
            >
              <Typography className={classes.categoryName}>{t(treatmentCategory.titleKey)}</Typography>
              {isSuggestedTreatmentTest && (
                <ToggleCategoryIcon
                  className={clsx(classes.toggleCategoryIcon, {
                    [classes.toggleCategoryCollapsed]: !categoriesToggle[treatmentCategory.titleKey],
                  })}
                />
              )}
            </div>
            {categoriesToggle[treatmentCategory.titleKey] && (
              <div className={classes.categoryTreatmentsContainer}>
                {treatmentCategory.treatmentBundles.map((treatment, treatmentIndex) => (
                  <TreatmentCard
                    treatment={treatment}
                    key={treatment.referenceId}
                    handleSelectTreatment={handleSelectTreatment}
                    isLast={treatmentIndex === treatmentCategory.treatmentBundles.length - 1}
                    selectedTreatmentId={selectedTreatmentId}
                  />
                ))}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
